import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Universal Button. Apple-flavoured: subtle scale on press, predictable
 * focus rings, every variant tuned for both light + dark, integrated
 * loading state so consumers don't have to bolt on Loader2 manually.
 *
 * Tactile press: `active:scale-[0.98]` is the iOS UIButton signature.
 * touch-manipulation kills the 300ms tap delay on mobile.
 *
 * Touch targets: `sm` keeps the 32px desktop look; on mobile breakpoints
 * the consumer should use `default` or `lg` (44px+ visual height).
 */

const buttonVariants = cva(
  [
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap',
    'font-medium rounded-[8px] transition-[background-color,color,border-color,transform,box-shadow,opacity]',
    'duration-150 ease-[cubic-bezier(0.32,0.72,0,1)]',
    'active:scale-[0.98] [touch-action:manipulation]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/50 focus-visible:ring-offset-2 ring-offset-[var(--color-background)]',
    'disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary action (CTA)
        primary:
          'bg-[var(--color-primary)] text-white shadow-lg shadow-[var(--color-primary-glow)] hover:bg-[var(--color-primary-hover)]',
        // Standard chromed button
        secondary:
          'bg-[var(--color-surface-elevated)] text-[var(--color-text)] border border-[var(--color-border)] shadow-sm hover:bg-[var(--color-surface-hover)]',
        // Quiet — toolbar buttons, navigation links
        ghost:
          'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]',
        // Translucent on top of imagery / 3D scenes
        glass:
          'bg-black/40 backdrop-blur-xl border border-white/10 text-white hover:bg-white/10',
        // Destructive (red)
        danger:
          'bg-[var(--color-destructive)] text-white shadow-sm hover:bg-[var(--color-destructive-hover)]',
        // Sidebar nav row
        nav:
          'text-[var(--color-text-muted)] justify-start font-medium hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text)]',
        // shadcn aliases
        default:
          'bg-[var(--color-primary)] text-white shadow-lg shadow-[var(--color-primary-glow)] hover:bg-[var(--color-primary-hover)]',
        destructive:
          'bg-[var(--color-destructive)] text-white shadow-sm hover:bg-[var(--color-destructive-hover)]',
        outline:
          'border border-[var(--color-border)] bg-transparent text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]',
        link:
          'text-[var(--color-primary)] underline-offset-4 hover:underline active:scale-100',
        // Legacy EVA aliases — DO NOT REMOVE
        'eva-primary':
          'bg-[var(--color-primary)] text-white shadow-lg shadow-[var(--color-primary-glow)] hover:bg-[var(--color-primary-hover)]',
        'eva-success':
          'bg-[var(--color-accent)] text-[var(--color-accent-foreground)] shadow-lg hover:bg-[var(--color-accent-hover)]',
        'eva-outline':
          'border border-[var(--color-border)] bg-transparent text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]',
      },
      size: {
        default: 'h-9 px-4 py-2 text-sm',
        sm: 'h-8 rounded-[6px] px-3 text-xs',
        md: 'h-9 px-4 py-2 text-sm',
        lg: 'h-11 rounded-[10px] px-6 text-base',
        // Hit-friendly mobile-first 44px height (Apple HIG)
        touch: 'h-11 rounded-[10px] px-5 text-sm',
        icon: 'h-9 w-9',
        'icon-sm': 'h-8 w-8 rounded-[6px]',
        'icon-touch': 'h-11 w-11 rounded-[10px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** When true, renders an inline spinner and disables the button. */
  isLoading?: boolean;
  /** Optional text shown next to the spinner during loading. */
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, isLoading, loadingText, children, disabled, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const showSpinner = !!isLoading;
    const content = showSpinner ? (
      <>
        <Loader2 className="animate-spin" />
        {loadingText ?? children}
      </>
    ) : (
      children
    );
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || showSpinner}
        aria-busy={showSpinner || undefined}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
